
// SAVING TEMPLATES

export type TemplateGroupArg = {
  type: "group",
  value: "all_targets" | "all_resources" | "all_indices",
  factors: { [key: string]: number }
};
export type TemplateSingleArg = {
  type: "item" | "variable" | "number",
  value: string,
  factor?: number
}
export type TemplateArg = TemplateSingleArg | TemplateGroupArg;



export const expressionTemplates = {
  "sum": {
    code: "sum" as const,
    maxArgs: null,
    factored: false,
    toExpression: (args: TemplateSingleArg[]) => {
      if (args.length === 0) return "0";
      const parts = args.flatMap(argToExpressionPart);
      return parts.join("+");
    }
  },
  "average": {
    code: "average" as const,
    maxArgs: null,
    factored: true,
    toExpression: (args: TemplateSingleArg[]) => {
      if (args.length === 0) return "0";
      const factorSum = args.reduce((sum, arg) => sum + (arg.factor ?? 1), 0);
      const parts = args.flatMap(argToExpressionPart);
      return `(${parts.join("+")}) / ${factorSum}`;
    }
  },
  "product": {
    code: "product" as const,
    maxArgs: null,
    factored: false,
    toExpression: (args: TemplateSingleArg[]) => {
      if (args.length === 0) return "0";
      const parts = args.flatMap(argToExpressionPart);
      return parts.join("*");
    }
  },
  "quotient": {
    code: "quotient" as const,
    maxArgs: null,
    factored: false,
    toExpression: (args: TemplateSingleArg[]) => {
      if (args.length === 0) return "0";
      const parts = args.flatMap(argToExpressionPart);
      return parts.join("/");
    }
  }
};

export const templateCodes = Object.keys(expressionTemplates) as TemplateCode[];

export type TemplateCode = keyof typeof expressionTemplates;

/**
 * 
 * Template string format:
 * tmpl_${template code}:[...template args as json array]
 * e.g.:
 * `tmpl_sum[ { "type": "group", "code": "all_targets", factors: { target_1: 4 } } ]`
 * `tmpl_average[]`
 * @param expression 
*/
export function parseTemplateExpression(expression: string): { code: TemplateCode | null, args: TemplateArg[] } {
  const match = expression.match(/^tmpl_(\w+)\:(.*)$/);
  if (!match) {
    return { code: null, args: [] };
  }
  const code = match[1] as TemplateCode;
  const args = JSON.parse(match[2]) as TemplateArg[];
  return { code, args };
}

export function encodeTemplateExpression(code: TemplateCode, args: TemplateArg[]): string {
  return `tmpl_${code}:${JSON.stringify(args)}`;
}


type ItemContext = {
  targets?: { [k: number]: { uid: `target_${number}` } },
  resources?: { [k: number]: { uid: `resource_${number}` } },
  indices?: { [k: number]: { uid: `index_${number}` } }
};

/**
 * Convert a template expression to a normal expression
 * @param expression The template expression
 */
export function templateExpressionToExpression(expression: string, itemContext: ItemContext): string {
  if (!expression.startsWith("tmpl_")) return expression;
  const { code, args } = parseTemplateExpression(expression);
  if (!code) return expression;
  const resolvedArgs = resolveGroups(args, itemContext);
  const decoded = expressionTemplates[code].toExpression(resolvedArgs);
  return decoded;
}

function argToExpressionPart(arg: TemplateSingleArg): string[] {
  if (arg.type === "item") {
    let val = arg.value;
    if (arg.factor != null && arg.factor != 1) val += "*" + arg.factor;
    return [val];
  } else if (arg.type === "variable") {
    let val = "${" + arg.value + "}";
    if (arg.factor != null && arg.factor != 1) val += "*" + arg.factor;
    return [val];
  } else if (arg.type === "number") {
    let val = arg.value;
    if (arg.factor != null && arg.factor != 1) val += "*" + arg.factor;
    return [val];
  }
  throw new Error("Unknown arg type: " + arg.type);
}

export function resolveGroups(args: TemplateArg[], itemContext: ItemContext) {
  return args.flatMap((arg) => {
    if (arg.type === "group") {
      return getArgsFromGroup(arg, itemContext);
    }
    return [arg];
  });
}

export function getArgsFromGroup(groupArg: TemplateGroupArg, context: ItemContext): TemplateSingleArg[] {
  if (groupArg.value == "all_targets") {
    const allTargets = Object.values(context.targets ?? {});
    return allTargets.map((target) => {
      const factor = groupArg.factors[target.uid];
      return { type: "item", value: target.uid, factor: factor };
    });
  }
  if (groupArg.value == "all_indices") {
    const allIndices = Object.values(context.indices ?? {});
    return allIndices.map((index) => {
      const factor = groupArg.factors[index.uid];
      return { type: "item", value: index.uid, factor: factor };
    });
  }
  if (groupArg.value == "all_resources") {
    const allResources = Object.values(context.resources ?? {});
    return allResources.map((resource) => {
      return {
        type: "item",
        value: resource.uid,
        get factor() {
          return groupArg.factors[resource.uid];
        }, set factor(value: number | undefined) {
          if (value == null) delete groupArg.factors[resource.uid];
          else {
            groupArg.factors[resource.uid] = value;
          }
        }
      };
    });
  }
  throw new Error("Unknown group arg value: " + groupArg.value);
}